import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors3 } from "../../colors/color";
import Printer2 from "../../components/Printer2";
import GraphLoader from "../../components/GraphLoader";
import { Grid, Button, ButtonGroup, Chip, Modal, Box, IconButton, Tooltip as MuiTooltip } from "@mui/material";
import { forecastingAcLineLossAction } from "../../actions/predictiveAction";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import NotesIcon from "@mui/icons-material/Notes";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";

const ForecastedAcLineLoss = ({ selectedLebel, databaseStr }) => {
  const {
    forecastedAcLineLoss,
    acLineLossLoading: loading,
  } = useSelector((state) => state.forecasting);

  const graphRef = useRef(null);
  
  // State for legend visibility
  const [legendItems, setLegendItems] = useState({
    "AC Loss": true,
    "Forecasted AC Loss": true,
  });

  // State for time period selection
  const [selectedPeriod, setSelectedPeriod] = useState("historical");
  const [selectedFilter, setSelectedFilter] = useState("all");
  
  // State for reason modal
  const [selectedReason, setSelectedReason] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get the current date range type from selectedLebel
  const currentDateRangeType = selectedLebel?.dateRangeType || "month";

  // Legend colors mapping
  const legendColors = {
    "AC Loss": colors3[0],
    "Forecasted AC Loss": "#203864",
  };

  // Handle legend click
  const handleLegendClick = (legendName) => {
    setLegendItems(prev => ({
      ...prev,
      [legendName]: !prev[legendName]
    }));
  };

  // Handle reason click
  const handleReasonClick = (date, reason) => {
    if (reason && reason.trim() !== "") {
      setSelectedReason({ date, reason });
      setIsModalOpen(true);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReason(null);
  };

  // Dynamic time period options based on dateRangeType
  const getTimePeriods = () => {
    switch (currentDateRangeType) {
      case "day":
        return [
          { label: "Historical", value: "historical", description: "View past data (daily)" },
          { label: "Next 7 Days", value: "7days", description: "Forecast next 7 days" },
          { label: "Next 30 Days", value: "30days", description: "Forecast next 30 days" },
        ];
      case "month":
        return [
          { label: "Historical", value: "historical", description: "View past data (monthly)" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
      case "year":
        return [
          { label: "Historical", value: "historical", description: "View past data (yearly)" },
          { label: "Next 2 Years", value: "2years", description: "Forecast next 2 years" },
          { label: "Next 5 Years", value: "5years", description: "Forecast next 5 years" },
        ];
      default:
        return [
          { label: "Historical", value: "historical", description: "View past data" },
          { label: "Next 6 Months", value: "6months", description: "Forecast next 6 months" },
          { label: "Next Year", value: "year", description: "Forecast next year" },
        ];
    }
  };

  const timePeriods = getTimePeriods();

  // Filter options configuration
  const filterOptions = [
    { value: "all", label: "Show All" },
    { value: "gt_0_25", label: "AC Loss > 0.25%" },
    { value: "gt_0_5", label: "AC Loss > 0.5%" },
  ];

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return null;
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (typeof num !== 'number' || isNaN(num)) {
      return null;
    }
    return num.toFixed(2);
  };

  // Custom bar component that shows blank space for null values but maintains bar width
  const CustomBar = (props) => {
    const { fill, x, y, width, height, value } = props;

    if (value === null || value === undefined || isNaN(value)) {
      return (
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill="transparent"
          stroke="none"
        />
      );
    }

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={fill}
        rx={2}
        ry={2}
      />
    );
  };

  // Custom tooltip component with reason display
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const periodOverPeriodChange = data["Period Over Period Change"];
      const periodOverPeriodPercentage = data["Period Over Period Percentage"];
      const reason = data["Reason"];

      const periodLabel = currentDateRangeType === "5_min" ? "5 Minutes" : 
                          currentDateRangeType === "day" ? "Day" :
                          currentDateRangeType === "year" ? "Year" : "Month";

      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "12px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            minWidth: "250px",
            maxWidth: "350px",
          }}
        >
          <p style={{ margin: "0 0 8px 0", fontWeight: "bold", fontSize: "0.9rem", borderBottom: "1px solid #eee", paddingBottom: "5px" }}>
            {label}
          </p>
          {payload.map((entry, index) => {
            const hasNoData = entry.value === null || entry.value === undefined || entry.value === "N/A";
            return (
              <p
                key={index}
                style={{
                  margin: "5px 0",
                  color: entry.color,
                  fontSize: "0.85rem",
                  fontStyle: hasNoData ? "italic" : "normal",
                }}
              >
                {entry.name}: <strong>{hasNoData ? "No Data" : `${formatNumber(entry.value)}%`}</strong>
              </p>
            );
          })}
          {periodOverPeriodChange !== null && periodOverPeriodPercentage !== null && (
            <p
              style={{
                margin: "8px 0 0 0",
                padding: "6px 8px",
                borderRadius: "4px",
                backgroundColor: periodOverPeriodChange >= 0 ? "#f6ffed" : "#fff2f0",
                border: `1px solid ${periodOverPeriodChange >= 0 ? "#b7eb8f" : "#ffccc7"}`,
                fontSize: "0.75rem",
                fontWeight: "bold",
                color: periodOverPeriodChange >= 0 ? "#389e0d" : "#a8071a",
              }}
            >
              vs Previous {periodLabel}:
              <span style={{ marginLeft: "5px" }}>
                {periodOverPeriodChange >= 0 ? "+" : ""}{formatNumber(periodOverPeriodChange)}%
                ({periodOverPeriodChange >= 0 ? "+" : ""}{formatNumber(periodOverPeriodPercentage)}%)
              </span>
            </p>
          )}
          {reason && reason.trim() !== "" && (
            <div
              style={{
                marginTop: "10px",
                padding: "8px",
                backgroundColor: "#e6f7ff",
                borderRadius: "4px",
                borderLeft: "3px solid #1890ff",
                cursor: "pointer",
              }}
              onClick={() => handleReasonClick(label, reason)}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "5px", marginBottom: "3px" }}>
                <NotesIcon style={{ fontSize: "12px", color: "#1890ff" }} />
                <span style={{ fontSize: "0.7rem", fontWeight: "bold", color: "#1890ff" }}>Notes/Reasons</span>
              </div>
              <p style={{ margin: "0", fontSize: "0.7rem", color: "#555", lineHeight: "1.4" }}>
                {reason.length > 100 ? reason.substring(0, 100) + "..." : reason}
              </p>
              <span style={{ fontSize: "0.65rem", color: "#1890ff", marginTop: "3px", display: "inline-block" }}>
                Click to view full details →
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Helper function to get slice limit based on period and date range type
  const getSliceLimit = (period) => {
    switch (period) {
      case "7days": return 7;
      case "30days": return 30;
      case "6months": return 6;
      case "year": return 12;
      case "2years": return 2;
      case "5years": return 5;
      default: return null;
    }
  };

  const minMaxRangeAndGraphData = useMemo(() => {
    if (forecastedAcLineLoss && forecastedAcLineLoss?.length > 0) {
      // Filter data based on selected time period
      let filteredData = [...forecastedAcLineLoss];

      if (selectedPeriod !== "historical") {
        const sliceLimit = getSliceLimit(selectedPeriod);
        if (sliceLimit && filteredData.length > sliceLimit) {
          filteredData = filteredData.slice(0, sliceLimit);
        }
      }

      // Create the base graph data with difference calculation
      const baseGraphData = filteredData?.reduce((acc, curr, index, array) => {
        let periodOverPeriodChange = null;
        let periodOverPeriodPercentage = null;

        // Calculate difference from previous period
        if (index > 0) {
          const currentLoss = curr["AC Loss"] || curr["Forecasted AC Loss"];
          const prevLoss = array[index - 1]["AC Loss"] || array[index - 1]["Forecasted AC Loss"];

          if (currentLoss != null && prevLoss != null) {
            const currentValue = typeof currentLoss === 'string'
              ? parseFloat(currentLoss)
              : currentLoss;
            const prevValue = typeof prevLoss === 'string'
              ? parseFloat(prevLoss)
              : prevLoss;

            periodOverPeriodChange = currentValue - prevValue;
            periodOverPeriodPercentage = prevValue !== 0
              ? (periodOverPeriodChange / prevValue) * 100
              : 0;
          }
        }

        const obj = { 
          Date: curr["Date"],
          Reason: curr["reason"] || null // Extract reason field
        };

        // Ensure numeric values (keep null as null)
        obj["AC Loss"] = curr["AC Loss"] != null && !isNaN(curr["AC Loss"])
          ? (typeof curr["AC Loss"] === 'string' ? parseFloat(curr["AC Loss"]) : curr["AC Loss"])
          : null;

        obj["Forecasted AC Loss"] = curr["Forecasted AC Loss"] != null && !isNaN(curr["Forecasted AC Loss"])
          ? (typeof curr["Forecasted AC Loss"] === 'string' ? parseFloat(curr["Forecasted AC Loss"]) : curr["Forecasted AC Loss"])
          : null;

        obj["Period Over Period Change"] = periodOverPeriodChange !== null ? Number(periodOverPeriodChange.toFixed(2)) : null;
        obj["Period Over Period Percentage"] = periodOverPeriodPercentage !== null ? Number(periodOverPeriodPercentage.toFixed(2)) : null;

        acc.push(obj);
        return acc;
      }, []) || [];

      // Apply filter based on selection
      const filteredGraphData = baseGraphData.filter((item) => {
        const acLoss = item["AC Loss"];
        const forecastedAcLoss = item["Forecasted AC Loss"];

        switch (selectedFilter) {
          case "gt_0_25":
            return (acLoss !== null && acLoss > 0.25) ||
              (forecastedAcLoss !== null && forecastedAcLoss > 0.25);
          case "gt_0_5":
            return (acLoss !== null && acLoss > 0.5) ||
              (forecastedAcLoss !== null && forecastedAcLoss > 0.5);
          case "all":
          default:
            return true;
        }
      });

      // Calculate min/max based on filtered data
      const allValues = [];
      filteredGraphData.forEach(item => {
        if (item["AC Loss"] != null && !isNaN(item["AC Loss"])) allValues.push(item["AC Loss"]);
        if (item["Forecasted AC Loss"] != null && !isNaN(item["Forecasted AC Loss"])) allValues.push(item["Forecasted AC Loss"]);
      });

      const unitLabel = currentDateRangeType === "day" ? "days" :
        currentDateRangeType === "year" ? "years" : "months";
      
      const hasAcLoss = filteredGraphData.some(item => item["AC Loss"] != null && !isNaN(item["AC Loss"]));
      const hasForecastedAcLoss = filteredGraphData.some(item => item["Forecasted AC Loss"] != null && !isNaN(item["Forecasted AC Loss"]));
      
      // Count items with reasons
      const itemsWithReasons = filteredGraphData.filter(item => item.Reason && item.Reason.trim() !== "").length;

      return {
        graphData: filteredGraphData,
        min: allValues.length > 0 ? Math.floor(Math.min(...allValues)) : 0,
        max: allValues.length > 0 ? Math.ceil(Math.max(...allValues)) : 1,
        totalCount: baseGraphData.length,
        filteredCount: filteredGraphData.length,
        unitLabel: unitLabel,
        hasAcLoss: hasAcLoss,
        hasForecastedAcLoss: hasForecastedAcLoss,
        itemsWithReasons: itemsWithReasons
      };
    }
    return { graphData: [], min: 0, max: 1, totalCount: 0, filteredCount: 0, unitLabel: "months", itemsWithReasons: 0 };
  }, [forecastedAcLineLoss, selectedPeriod, selectedFilter, currentDateRangeType]);

  // Custom label formatter for bar labels
  const renderBarLabel = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "";
    return formatNumber(value);
  };

  const dispatch = useDispatch();
  const location = useLocation();

  // Helper function to get forecast value based on selected period
  const getForecastValue = (period) => {
    switch (period) {
      case "historical": return "historical";
      case "7days": return "7days";
      case "30days": return "30days";
      case "6months": return "6months";
      case "year": return "year";
      case "2years": return "2years";
      case "5years": return "5years";
      default: return "historical";
    }
  };

  useEffect(() => {
    if (!selectedPeriod || !selectedLebel?.dateRangeType) return;

    const timer = setTimeout(() => {
      const forecastValue = getForecastValue(selectedPeriod);
      dispatch(
        forecastingAcLineLossAction(
          { ...selectedLebel, forecast: forecastValue },
          databaseStr
        )
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [
    location.pathname,
    selectedPeriod,
    selectedLebel?.dateRangeType,
    databaseStr,
    dispatch
  ]);

  // Reset selected period when date range type changes
  useEffect(() => {
    setSelectedPeriod("historical");
  }, [currentDateRangeType]);

  // Handle period change
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    setSelectedFilter(e.target.value);
  };

  // Get display unit for the title
  const getDisplayUnit = () => {
    switch (currentDateRangeType) {
      case "day": return "Daily";
      case "month": return "Monthly";
      case "year": return "Yearly";
      default: return "Monthly";
    }
  };

  // Custom XAxis tick renderer to show reason indicator
  const renderXAxisTick = (props) => {
    const { x, y, payload } = props;
    const dataPoint = minMaxRangeAndGraphData.graphData?.find(d => d.Date === payload.value);
    const hasReason = dataPoint?.Reason && dataPoint.Reason.trim() !== "";
    
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#666"
          fontSize="0.75rem"
          transform="rotate(-45)"
        >
          {payload.value}
        </text>
        {hasReason && (
          <text
            x={-5}
            y={-5}
            textAnchor="end"
            fill="#1890ff"
            fontSize="12px"
            cursor="pointer"
            onClick={() => handleReasonClick(payload.value, dataPoint.Reason)}
          >
            📝
          </text>
        )}
      </g>
    );
  };

  return (
    <>
      <Grid container xl={12} textAlign={"center"}>
        <Grid container xl={12}>
          <Grid
            item
            xl={11.8}
            lg={11.8}
            md={11.8}
            sm={11.8}
            xs={11.8}
            style={{
              background: "linear-gradient(to bottom, rgb(3, 99, 125),rgb(4, 128, 162),rgb(4, 135, 172))",
              margin: "0 auto 0.5vmax",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.5vmax",
            }}
          >
            <Grid
              item
              xs={12}
              style={{
                background: "white",
                padding: "0.5vmax 0.5vmax",
                position: "relative",
                overflow: "auto",
              }}
            >
              {/* Fixed Header - Sticky */}
              <div
                style={{
                  padding: "1rem 1.5rem",
                  position: "sticky",
                  left: "0",
                  right: "0",
                  top: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  textAlign: "center",
                  zIndex: "10",
                  backgroundColor: "white",
                  borderBottom: "1px solid #f0f0f0",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                {/* Left side - Time Period Selection */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" }}>
                  <label style={{ fontSize: "0.9rem", fontWeight: 500, color: "#333" }}>
                    Time Period ({getDisplayUnit()}):
                  </label>
                  <ButtonGroup variant="outlined" size="small" style={{ display: "flex", flexWrap: "wrap" }}>
                    {timePeriods.map((period) => (
                      <Button
                        key={period.value}
                        onClick={() => handlePeriodChange(period.value)}
                        variant={selectedPeriod === period.value ? "contained" : "outlined"}
                        style={{
                          backgroundColor: selectedPeriod === period.value ? "#1890ff" : "transparent",
                          color: selectedPeriod === period.value ? "white" : "#666",
                          borderColor: "#d0d0d0",
                          fontSize: "0.8rem",
                          padding: "4px 12px",
                          minWidth: "90px",
                        }}
                        title={period.description}
                      >
                        {period.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>

                {/* Center - Title */}
                <div style={{position:"absolute",left:0,right:0, flex: 1, textAlign: "center" }}>
                  <h5 style={{ margin: "0 0 5px 0" }}>
                    {selectedPeriod === "historical" ? "Historical AC Loss (%) - " + getDisplayUnit() + " Data" : "Forecasted AC Loss (%) - " + getDisplayUnit() + " Data"}
                    {selectedFilter !== "all" && (
                      <Chip
                        label="Filtered"
                        size="small"
                        style={{
                          marginLeft: "8px",
                          backgroundColor: "#fff2e8",
                          color: "#fa541c",
                          borderColor: "#ffbb96",
                          fontSize: "0.7rem",
                          height: "20px",
                        }}
                        variant="outlined"
                      />
                    )}
                    {/* {minMaxRangeAndGraphData.itemsWithReasons > 0 && (
                      <Chip
                        icon={<NotesIcon style={{ fontSize: "12px" }} />}
                        label={`${minMaxRangeAndGraphData.itemsWithReasons} notes available`}
                        size="small"
                        style={{
                          marginLeft: "8px",
                          backgroundColor: "#e6f7ff",
                          color: "#1890ff",
                          borderColor: "#91d5ff",
                          fontSize: "0.7rem",
                          height: "20px",
                        }}
                        variant="outlined"
                      />
                    )} */}
                  </h5>
                </div>

                {/* Right side - Filter and Printer */}
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem", justifyContent: "center",zIndex:1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <label htmlFor="ac-line-loss-filter" style={{ fontSize: "0.85rem", fontWeight: 500, color: "#333" }}>
                      Filter:
                    </label>
                    <select
                      id="ac-line-loss-filter"
                      value={selectedFilter}
                      onChange={handleFilterChange}
                      style={{
                        padding: "0.4rem 0.8rem",
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        backgroundColor: "white",
                        cursor: "pointer",
                        minWidth: "160px",
                      }}
                    >
                      {filterOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>

                    {selectedFilter !== "all" && minMaxRangeAndGraphData.totalCount > 0 && (
                      <Chip
                        label={`${minMaxRangeAndGraphData.filteredCount}/${minMaxRangeAndGraphData.totalCount} ${minMaxRangeAndGraphData.unitLabel}`}
                        size="small"
                        style={{
                          fontSize: "0.7rem",
                          backgroundColor: "#e6f7ff",
                          color: "#1890ff",
                          borderColor: "#91d5ff",
                          height: "22px",
                        }}
                        variant="outlined"
                      />
                    )}
                  </div>
                 
                  <Printer2
                    dataSetDetail={{
                      data: minMaxRangeAndGraphData.graphData,
                      name: `Forecasted AC Loss - ${getDisplayUnit()} (${selectedPeriod !== "historical" ? "Forecasted" : "Historical"})`,
                      ref: graphRef,
                      refCurrent: graphRef.current?.container,
                    }}
                  />
                </div>
              </div>

              {/* Custom Clickable Legend Container - Sticky */}
              <div
                style={{
                  position: "sticky",
                  left: "0",
                  top: "85px",
                  zIndex: "1",
                  backgroundColor: "rgba(255, 255, 255, 0.98)",
                  padding: "12px 20px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "40px",
                  flexWrap: "wrap",
                  // borderBottom: "1px solid #f0f0f0",
                  // boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                }}
              >
                {Object.keys(legendItems).map((legendName) => {
                  const hasData = legendName === "AC Loss" 
                    ? minMaxRangeAndGraphData.hasAcLoss 
                    : minMaxRangeAndGraphData.hasForecastedAcLoss;
                  
                  return !hasData ? null : (
                    <div
                      key={legendName}
                      onClick={() => handleLegendClick(legendName)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        userSelect: "none",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        backgroundColor: legendItems[legendName] ? "transparent" : "#f5f5f5",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          backgroundColor: legendColors[legendName],
                          borderRadius: "3px",
                          opacity: legendItems[legendName] ? 1 : 0.4,
                        }}
                      />
                      <span
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: 500,
                          color: legendItems[legendName] ? "#333" : "#999",
                          textDecoration: legendItems[legendName] ? "none" : "line-through",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {legendName}
                      </span>
                    </div>
                  );
                })}
                {
                  selectedPeriod !== "historical" && minMaxRangeAndGraphData.itemsWithReasons > 0 && 
                
                <MuiTooltip title="Dates with a 📝 icon have additional notes/reasons. Click on the icon or tooltip to view details." arrow>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", marginLeft: "15px" }}>
                    <span style={{ fontSize: "0.7rem", color: "#666" }}>📝 Notes Indicator</span>
                  </div>
                </MuiTooltip>
}
              </div>

              {/* Graph Container */}
              <div
                style={{
                  minWidth: "100%",
                  width: minMaxRangeAndGraphData.graphData?.length > 0
                    ? 2.5 * minMaxRangeAndGraphData.graphData.length * 55 + "px"
                    : "100%",
                  
                }}
                className="forecasting-ac-line-loss-container"
              >
                <ResponsiveContainer width="100%" height={"100%"}>
                  {loading ? (
                    <div style={{ position: "absolute", top: "0", bottom: "0", right: "0", left: "0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <GraphLoader content={`${selectedPeriod !== "historical" ? "Forecasting" : "Loading"} AC Loss`} />
                    </div>
                  ) : minMaxRangeAndGraphData.graphData?.length > 0 ? (
                    <ComposedChart
                      barCategoryGap={"10%"}
                      barGap={5}
                      ref={graphRef}
                      margin={{ top: 15, bottom: 5, left: 5, right: 5 }}
                      data={minMaxRangeAndGraphData.graphData || []}
                    >
                      <XAxis
                        fontSize={"0.8rem"}
                        fontWeight={600}
                        dataKey="Date"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={5}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                        interval={0}
                        tick={renderXAxisTick}
                      />
                      <CartesianGrid stroke="grey" strokeWidth={0.3} />

                      <YAxis
                        type="number"
                        fontSize={"0.8rem"}
                        fontWeight={600}
                        tickLine={false}
                        tickMargin={5}
                        tickCount={6}
                        domain={[minMaxRangeAndGraphData.min, minMaxRangeAndGraphData.max]}
                        label={{
                          value: "AC Loss (%)",
                          dy:30,
                          angle: -90,
                          position: "insideLeft",
                          style: { fontSize: "0.8rem", fontWeight: "bold" },
                        }}
                      />

                      <Tooltip cursor={{ fill: "none" }} content={<CustomTooltip />} contentStyle={{ fontSize: "0.7rem" }} />

                      {/* AC Loss Bar */}
                      {minMaxRangeAndGraphData.hasAcLoss && (
                        <Bar
                          hide={!legendItems["AC Loss"]}
                          dataKey="AC Loss"
                          fill={legendColors["AC Loss"]}
                          barSize={35}
                          shape={<CustomBar />}
                        >
                          <LabelList
                            dataKey="AC Loss"
                            position="top"
                            fontSize={"0.8rem"}
                            fontWeight={600}
                            formatter={renderBarLabel}
                          />
                        </Bar>
                      )}

                      {/* Forecasted AC Loss Bar */}
                      {minMaxRangeAndGraphData.hasForecastedAcLoss && (
                        <Bar
                          hide={!legendItems["Forecasted AC Loss"]}
                          dataKey="Forecasted AC Loss"
                          fill={legendColors["Forecasted AC Loss"]}
                          barSize={35}
                          shape={<CustomBar />}
                        >
                          <LabelList
                            dataKey="Forecasted AC Loss"
                            position="top"
                            fontSize={"0.8rem"}
                            fontWeight={600}
                            formatter={renderBarLabel}
                          />
                        </Bar>
                      )}
                    </ComposedChart>
                  ) : (
                    <div style={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "1rem" }}>
                      <h3>
                        {selectedFilter !== "all"
                          ? "No data matches the filter criteria"
                          : "No data available for the selected period"}
                      </h3>
                      {selectedFilter !== "all" && (
                        <button
                          onClick={() => setSelectedFilter("all")}
                          style={{
                            padding: "0.5rem 1rem",
                            backgroundColor: "#1890ff",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                          }}
                        >
                          Show All Data
                        </button>
                      )}
                    </div>
                  )}
                </ResponsiveContainer>
              </div>

              {/* Summary Section */}
              {/* {minMaxRangeAndGraphData.graphData?.length > 0 && (
                <div
                  style={{
                    padding: "1rem",
                    borderTop: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "space-around",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "1rem",
                    marginTop: "1rem",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                      Total {minMaxRangeAndGraphData.unitLabel === "days" ? "Days" :
                               minMaxRangeAndGraphData.unitLabel === "years" ? "Years" : "Months"}
                    </div>
                    <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#1890ff" }}>
                      {minMaxRangeAndGraphData.totalCount}
                    </div>
                  </div>

                  {selectedFilter !== "all" && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                        {minMaxRangeAndGraphData.unitLabel === "days" ? "Days" :
                         minMaxRangeAndGraphData.unitLabel === "years" ? "Years" : "Months"} Meeting Filter
                      </div>
                      <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#fa541c" }}>
                        {minMaxRangeAndGraphData.filteredCount}
                      </div>
                    </div>
                  )}

                  {minMaxRangeAndGraphData.itemsWithReasons > 0 && (
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                        Notes Available
                      </div>
                      <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#fa541c" }}>
                        {minMaxRangeAndGraphData.itemsWithReasons}
                      </div>
                    </div>
                  )}

                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                      Selected Period
                    </div>
                    <div style={{ fontSize: "1rem", fontWeight: "500", color: "#333" }}>
                      {timePeriods.find(p => p.value === selectedPeriod)?.label}
                    </div>
                  </div>

                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "0.8rem", color: "#666", marginBottom: "4px" }}>
                      Data Type
                    </div>
                    <div style={{ fontSize: "1rem", fontWeight: "500", color: "#333" }}>
                      {getDisplayUnit()}
                    </div>
                  </div>
                </div>
              )} */}
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Reason Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="reason-modal-title"
        aria-describedby="reason-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 500, md: 600 },
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            maxHeight: '80vh',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #e0e0e0',
              p: 2,
              bgcolor: '#f5f5f5',
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <NotesIcon style={{ color: "#1890ff" }} />
              <h3 id="reason-modal-title" style={{ margin: 0, fontSize: "1.1rem" }}>
                Notes / Reasons
              </h3>
            </div>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ p: 3, maxHeight: 'calc(80vh - 70px)', overflowY: 'auto' }}>
            {selectedReason && (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <span style={{ fontSize: "0.75rem", color: "#999", textTransform: "uppercase" }}>Date</span>
                  <p style={{ margin: "4px 0 0 0", fontSize: "1rem", fontWeight: 500, color: "#333" }}>
                    {selectedReason.date}
                  </p>
                </div>
                <div>
                  <span style={{ fontSize: "0.75rem", color: "#999", textTransform: "uppercase" }}>Details</span>
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "12px",
                      backgroundColor: "#f9f9f9",
                      borderRadius: "8px",
                      borderLeft: "3px solid #1890ff",
                      fontSize: "0.9rem",
                      lineHeight: "1.6",
                      color: "#555",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedReason.reason}
                  </div>
                </div>
              </>
            )}
          </Box>
          <Box
            sx={{
              borderTop: '1px solid #e0e0e0',
              p: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              bgcolor: '#fafafa',
            }}
          >
            <Button onClick={handleCloseModal} variant="contained" size="small" style={{ backgroundColor: "#1890ff" }}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ForecastedAcLineLoss;